import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ActivityFeed from '@/components/organisms/Tile/TileActivityLog/ActivityFeed/ActivityFeed';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { useActivityLogs } from '@/hooks/useActivityLogs';
import { selectAccountSlug } from '@/redux/session/selectors';

import { ActivityLogToolbar } from './components/ActivityLogToolbar';
import { useActivityLogsPage } from './hooks/useActivityLogsPage';

const HOME_LIMIT = 5;

interface EmbeddedProps {
  deskId?: string | null;
  brain_ids?: string[];
  brainId?: string | null;
}

/** Home embedding: prop-driven scoping, no toolbar, limited feed. */
const ActivityLogsEmbedded = ({ deskId, brain_ids, brainId }: EmbeddedProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { data } = useActivityLogs(slug, { deskId, brainIds: brain_ids, brainId });

  const allLogs = useMemo(
    () => (data?.pages || []).flatMap((page) => page.audit_logs || []),
    [data?.pages]
  );

  return (
    <ActivityFeed
      logs={allLogs}
      limit={HOME_LIMIT}
      isLoading={!data}
      emptyMessage={t('activityLogs.noLogs')}
      hasMore={false}
    />
  );
};

/** Full page: chip-based filter bar + paginated feed. */
const ActivityLogsFull = () => {
  const { t } = useTranslation();
  const page = useActivityLogsPage();
  useTitle(t('pages.activity_logs'));

  // Flatten all loaded pages so a run that straddles a pagination boundary stays one group;
  // ActivityFeed handles grouping, bucketing, and the day dividers.
  const allLogs = useMemo(
    () => (page.data?.pages || []).flatMap((p) => p.audit_logs || []),
    [page.data?.pages]
  );

  return (
    <SettingsContent className="max-w-5xl min-w-md">
      <ActivityLogToolbar page={page} />
      <div className="px-4">
        <ActivityFeed
          logs={allLogs}
          isLoading={!page.data}
          emptyMessage={t('activityLogs.noLogs')}
          emptySubMessage={t('activityLogs.noLogsDescription')}
          onShowMore={page.fetchNextPage}
          hasMore={page.hasNextPage === true}
        />
      </div>
    </SettingsContent>
  );
};

interface Props {
  hideFilters?: boolean;
  deskId?: string | null;
  brain_ids?: string[];
  brainId?: string | null;
}

export const ActivityLogs = ({ hideFilters, deskId, brain_ids, brainId }: Props) => {
  if (hideFilters) {
    return <ActivityLogsEmbedded deskId={deskId} brain_ids={brain_ids} brainId={brainId} />;
  }
  return <ActivityLogsFull />;
};
